import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import { FlagOption, languages } from '../../../components/LanguageSelector';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { maskedInputSlotProps } from '../../../components/dashboard/MaskedInput';
import {
  CityAutocomplete,
  CountryAutocomplete,
  PostalCodeField,
  StateAutocomplete,
  useAddressLocations,
} from '../../../components/dashboard/LocationFields';
import usePostalCodeLookup from '../../../components/dashboard/usePostalCodeLookup';
import { nextPhoneInput, POSTAL_CODE_MASKS, TAX_ID_MASKS, toE164 } from '../../../utils/formatters';
import {
  ADDRESS_FIELDS,
  COMPANY_FIELDS,
  COMPANY_FIELD_HELPER_KEY,
  COMPANY_FIELD_LABEL_KEY,
  normalizeTaxId,
  REQUIRED_ADDRESS_FIELDS,
  REQUIRED_COMPANY_FIELDS,
  COMPANY_FIELD_RULES,
  normalizeCountry,
  validateFieldValue,
} from '../../../components/dashboard/companyFields';

const IDENTIFICATION_FIELDS = ['display_name', 'provider_uuid', 'language'];

const EDIT_FIELDS = [...IDENTIFICATION_FIELDS, ...COMPANY_FIELDS];
const CREATE_FIELDS = [...EDIT_FIELDS, ...ADDRESS_FIELDS];

// Required by the backend (blank not allowed); everything else is optional.
const REQUIRED_EDIT = ['display_name', 'provider_uuid', ...REQUIRED_COMPANY_FIELDS];
const REQUIRED_CREATE = [...REQUIRED_EDIT, ...REQUIRED_ADDRESS_FIELDS];

const FIELD_LABEL_KEY = {
  display_name: 'displayName',
  provider_uuid: 'provider',
  language: 'language',
  ...COMPANY_FIELD_LABEL_KEY,
};

const FIELD_HELPER_KEY = {
  display_name: 'displayNameHelper',
  provider_uuid: 'providerHelper',
  language: 'languageHelper',
  ...COMPANY_FIELD_HELPER_KEY,
};

// Applied on every keystroke/paste, so the field never holds anything else.
// Normalizers get (next value, previous value).
const FIELD_NORMALIZERS = {
  persona_tax_id: normalizeTaxId,
  persona_country: normalizeCountry,
  address_country: normalizeCountry,
  // Edited formatted ("+55 11 99876-5432"), sent as E.164 (buildPayload).
  phone_number: (next, previous) => nextPhoneInput(previous, next),
};

// Input masks by the data's country (the value in state stays raw).
const FIELD_MASKS = {
  persona_tax_id: (values) => TAX_ID_MASKS[values.persona_country],
  // The address country is the company's (see handleCompanyCountryChange).
  address_postal_code: (values) => POSTAL_CODE_MASKS[values.persona_country],
};

// Max length / type of each input, matching the Django models (Customer +
// the company ones shared with the business form).
const FIELD_RULES = {
  display_name: { maxLength: 255 },
  ...COMPANY_FIELD_RULES,
};

const emptyValues = () => ({
  ...Object.fromEntries(CREATE_FIELDS.map((field) => [field, ''])),
  persona_country: 'BR',
  address_country: 'BR',
});

const valuesFromCustomer = (customer) => ({
  ...emptyValues(),
  display_name: customer.display_name ?? '',
  provider_uuid: customer.provider_uuid ?? '',
  language: customer.language ?? '',
  persona_full_name: customer.persona?.full_name ?? '',
  persona_legal_name: customer.persona?.legal_name ?? '',
  // Older records may still carry punctuation; show (and save) them clean.
  persona_tax_id: normalizeTaxId(customer.persona?.tax_id ?? ''),
  persona_registration_number: customer.persona?.registration_number ?? '',
  persona_email: customer.persona?.email ?? '',
  persona_website: customer.persona?.website ?? '',
  persona_country: customer.persona?.country ?? '',
});

export default function CustomerForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  // Create/edit are internal-only (the backend enforces it with
  // IsInternalUser); skip the form entirely for everyone else.
  const isInternal = Boolean(collaborator?.is_internal);

  const customerQuery = useQuery({
    queryKey: ['customers', id],
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomer(id).then((res) => res.data),
    enabled: isEditMode && isInternal,
  });

  const providersQuery = useQuery({
    queryKey: ['providers'],
    // Providers endpoint uses the global pagination; unwrap `results`.
    queryFn: () => customerService.getProviders().then((res) => res.data.results ?? res.data),
    enabled: isInternal,
  });
  const providers = useMemo(() => providersQuery.data ?? [], [providersQuery.data]);

  const [values, setValues] = useState(emptyValues);
  const [isActive, setIsActive] = useState(true);
  const [isHeadquarters, setIsHeadquarters] = useState(true);
  const [createFirstBusiness, setCreateFirstBusiness] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState(false);
  // Fields the user already left.
  const [blurred, setBlurred] = useState({});
  const [flashMessage, setFlashMessage] = useState('');

  // State/city option lists (by address country) and postal code autofill.
  const locations = useAddressLocations({ values, setValues, countryField: 'persona_country' });
  const numberInputRef = useRef(null);
  const postalCode = usePostalCodeLookup({
    // A new postal code replaces street/district/city/state; number and
    // complement are kept.
    onFound: (address) => {
      setValues((prev) => ({
        ...prev,
        address_street: address.street,
        address_district: address.district,
        address_city: address.city,
        address_state: address.state,
      }));
      setFieldErrors((prev) => ({
        ...prev,
        address_street: undefined,
        address_district: undefined,
        address_city: undefined,
        address_state: undefined,
      }));
      numberInputRef.current?.focus();
    },
  });

  useEffect(() => {
    if (isEditMode && customerQuery.data) {
      setValues(valuesFromCustomer(customerQuery.data));
      setIsActive(customerQuery.data.is_active);
    }
  }, [isEditMode, customerQuery.data]);

  // Single provider (the usual case): preselect it on create.
  useEffect(() => {
    if (!isEditMode && providers.length === 1 && !values.provider_uuid) {
      setValues((prev) => ({ ...prev, provider_uuid: providers[0].uuid }));
    }
  }, [isEditMode, providers, values.provider_uuid]);

  const requiredFields = isEditMode ? REQUIRED_EDIT : REQUIRED_CREATE;
  const fieldIssue = (field) =>
    validateFieldValue(values[field], { required: requiredFields.includes(field), ...FIELD_RULES[field] }) ||
    locations.issue(field);
  // address_country isn't a field of its own: it mirrors persona_country.
  const isValid = (isEditMode ? EDIT_FIELDS : CREATE_FIELDS)
    .filter((field) => field !== 'address_country')
    .every((field) => !fieldIssue(field));

  const setField = (field) => (event) => {
    const normalize = FIELD_NORMALIZERS[field];
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: normalize ? normalize(value, prev[field]) : value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setFieldValue = (field, value) => setField(field)({ target: { value } });

  // The address uses the company's country (no separate address country
  // for now — covers most cases). Cascade: another country means other
  // states/cities (and postal code format); another state, other cities.
  const handleCompanyCountryChange = (country) => {
    setValues((prev) => ({
      ...prev,
      persona_country: country,
      address_country: country,
      address_state: '',
      address_city: '',
    }));
    setFieldErrors((prev) => ({ ...prev, persona_country: undefined, address_country: undefined }));
    postalCode.reset();
  };

  const handleAddressStateChange = (state) => {
    setValues((prev) => ({ ...prev, address_state: state, address_city: '' }));
    setFieldErrors((prev) => ({ ...prev, address_state: undefined }));
  };

  const handlePostalCodeChange = (event) => {
    setField('address_postal_code')(event);
    postalCode.lookup(values.persona_country, event.target.value);
  };

  const fieldProps = (field) => {
    // Client-side problems show once the user left the field or tried to save.
    const issue = (touched || blurred[field]) && fieldIssue(field);
    const helperKey = FIELD_HELPER_KEY[field];
    const { maxLength } = FIELD_RULES[field] ?? {};
    return {
      label: t(`customers.fields.${FIELD_LABEL_KEY[field]}`),
      value: values[field],
      onChange: setField(field),
      onBlur: () => setBlurred((prev) => (prev[field] ? prev : { ...prev, [field]: true })),
      required: requiredFields.includes(field),
      error: Boolean(issue || fieldErrors[field]),
      // Always some helper text (' ' when there's no hint): the line is
      // reserved, so an error showing up doesn't push the layout (and the
      // save button the user is clicking) down.
      helperText: issue
        ? t(`customers.validation.${issue.key}`, issue.params)
        : fieldErrors[field] || (helperKey ? t(`customers.fields.${helperKey}`) : ' '),
      slotProps: maskedInputSlotProps(FIELD_MASKS[field]?.(values), maxLength ? { maxLength } : undefined),
      fullWidth: true,
    };
  };

  const resetFormFields = () => {
    setValues((prev) => ({ ...emptyValues(), provider_uuid: providers.length === 1 ? prev.provider_uuid : '' }));
    setIsHeadquarters(true);
    setCreateFirstBusiness(true);
    setTouched(false);
    setBlurred({});
  };

  const buildPayload = () => {
    const fields = isEditMode ? EDIT_FIELDS : CREATE_FIELDS;
    const payload = Object.fromEntries(fields.map((field) => [field, values[field].trim()]));
    if ('phone_number' in payload) payload.phone_number = toE164(payload.phone_number);
    if ('address_country' in payload) payload.address_country = values.persona_country;
    if (isEditMode) {
      payload.is_active = isActive;
    } else {
      payload.persona_is_headquarters = isHeadquarters;
      payload.create_first_business = createFirstBusiness;
    }
    return payload;
  };

  const mutation = useMutation({
    mutationFn: () =>
      isEditMode ? customerService.patchCustomer(id, buildPayload()) : customerService.createCustomer(buildPayload()),
    // `intent` is whichever submit button was clicked — 'another' only
    // exists in create mode (see the extra button in CardActions).
    onSuccess: (response, intent) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (isEditMode) {
        navigate(`/customers/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('customers.saveSuccessCreate'));
        return;
      }
      navigate('/customers', { state: { flashMessage: t('customers.saveSuccessCreate') } });
    },
    onError: (error) => {
      const known = isEditMode ? EDIT_FIELDS : CREATE_FIELDS;
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(error, known);
      setFieldErrors(apiFieldErrors);
      if (error?.response?.status === 403) {
        setFormError(t('customers.states.permissionDeniedMessage'));
        return;
      }
      setFormError(apiFormError || (Object.keys(apiFieldErrors).length ? '' : t('customers.saveError')));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate(intent);
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('customers.editTitle') : t('customers.createTitle')}
      breadcrumbs={[
        { label: t('customers.breadcrumb.dashboard') },
        { label: t('nav.groups.admin') },
        { label: t('customers.breadcrumb.list'), to: '/customers' },
        { label: isEditMode ? t('customers.breadcrumb.edit') : t('customers.breadcrumb.new') },
      ]}
    />
  );

  if (!isInternal) {
    return (
      <BoundedPageLayout header={header}>
        <PermissionDenied
          title={t('customers.states.permissionDeniedTitle')}
          message={t('customers.states.permissionDeniedMessage')}
        />
      </BoundedPageLayout>
    );
  }

  // Wait for providers too: the loaded customer's provider_uuid would
  // otherwise hit the Select before its options exist (MUI out-of-range).
  if (isEditMode && (customerQuery.isLoading || providersQuery.isLoading)) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('customers.states.loading')} />
      </BoundedPageLayout>
    );
  }

  if (isEditMode && customerQuery.isError) {
    const httpStatus = customerQuery.error?.response?.status;
    return (
      <BoundedPageLayout header={header}>
        {httpStatus === 403 && (
          <PermissionDenied
            title={t('customers.states.permissionDeniedTitle')}
            message={t('customers.states.permissionDeniedMessage')}
          />
        )}
        {httpStatus === 404 && (
          <ErrorState title={t('customers.states.notFoundTitle')} message={t('customers.states.notFoundMessage')} />
        )}
        {httpStatus !== 403 && httpStatus !== 404 && (
          <ErrorState
            title={t('customers.states.errorTitle')}
            message={t('customers.states.errorMessage')}
            actionLabel={t('customers.states.retry')}
            onRetry={customerQuery.refetch}
          />
        )}
      </BoundedPageLayout>
    );
  }

  const actions = (
    <>
      <Divider />
      <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
        {/* Back to wherever the user came from (list or the customer's own
            detail page) — "Cancel" means undo getting here. */}
        <Button type="button" color="inherit" onClick={() => navigate(-1)}>
          {t('customers.actions.cancel')}
        </Button>
        {!isEditMode && (
          <Button type="submit" name="intent" value="another" variant="outlined" disabled={mutation.isPending}>
            {t('customers.actions.saveAndAddAnother')}
          </Button>
        )}
        <Button type="submit" name="intent" value="list" variant="contained" disabled={mutation.isPending}>
          {isEditMode ? t('customers.actions.saveEdit') : t('customers.actions.saveCreate')}
        </Button>
      </CardActions>
    </>
  );

  const errorAlert = formError && <Alert severity="error">{formError}</Alert>;

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} noValidate spacing={3} sx={{ width: '100%', pb: 3 }}>
        <Card>
          <CardHeader
            title={t('customers.sections.identification')}
            subheader={t('customers.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('display_name')} />
                <TextField select {...fieldProps('provider_uuid')}>
                  {providers.map((option) => (
                    <MenuItem key={option.uuid} value={option.uuid}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                select
                {...fieldProps('language')}
                // '' is a real choice here ("no default language"), so show it
                // instead of an empty-looking field.
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    const language = languages.find((option) => option.code === value);
                    return language ? <FlagOption language={language} /> : t('customers.fields.languageNone');
                  },
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}
              >
                <MenuItem value="">{t('customers.fields.languageNone')}</MenuItem>
                {languages.map((language) => (
                  <MenuItem key={language.code} value={language.code}>
                    <FlagOption language={language} />
                  </MenuItem>
                ))}
              </TextField>
              {isEditMode && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack>
                    <Typography variant="body1">{t('customers.fields.isActive')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.fields.isActiveHelper')}
                    </Typography>
                  </Stack>
                  <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('customers.sections.company')}
            subheader={t('customers.sections.companyHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              {/* Country first: it drives the tax ID mask and the address
                  (postal code, states and cities). */}
              <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                <CountryAutocomplete
                  value={values.persona_country}
                  onChange={handleCompanyCountryChange}
                  textFieldProps={fieldProps('persona_country')}
                />
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('persona_full_name')} />
                <TextField {...fieldProps('persona_legal_name')} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('persona_tax_id')} />
                <TextField {...fieldProps('persona_registration_number')} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField type="email" {...fieldProps('persona_email')} />
                <TextField type="url" {...fieldProps('persona_website')} />
              </Stack>
              {!isEditMode && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack>
                    <Typography variant="body1">{t('customers.fields.isHeadquarters')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.fields.isHeadquartersHelper')}
                    </Typography>
                  </Stack>
                  <Switch checked={isHeadquarters} onChange={(event) => setIsHeadquarters(event.target.checked)} />
                </Stack>
              )}
              {isEditMode && errorAlert}
            </Stack>
          </CardContent>
          {isEditMode && actions}
        </Card>

        {!isEditMode && (
          <Card>
            <CardHeader
              title={t('customers.sections.address')}
              subheader={t('customers.sections.addressHint')}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <CardContent>
              <Stack spacing={2.5}>
                {/* Postal code first: it fills the rest (country = the company's). */}
                <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                  <PostalCodeField
                    status={postalCode.status}
                    fieldProps={{ ...fieldProps('address_postal_code'), onChange: handlePostalCodeChange }}
                  />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField {...fieldProps('address_street')} />
                  <TextField
                    {...fieldProps('address_number')}
                    inputRef={numberInputRef}
                    sx={{ maxWidth: { sm: 160 } }}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField {...fieldProps('address_complement')} />
                  <TextField {...fieldProps('address_district')} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <StateAutocomplete
                      locations={locations.states}
                      value={values.address_state}
                      onChange={handleAddressStateChange}
                      textFieldProps={fieldProps('address_state')}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CityAutocomplete
                      locations={locations.cities}
                      state={values.address_state}
                      value={values.address_city}
                      onChange={(city) => setFieldValue('address_city', city)}
                      textFieldProps={fieldProps('address_city')}
                    />
                  </Box>
                </Stack>
                <TextField type="tel" {...fieldProps('phone_number')} />
              </Stack>
            </CardContent>
          </Card>
        )}

        {!isEditMode && (
          <Card>
            <CardHeader
              title={t('customers.sections.firstBusiness')}
              subheader={t('customers.sections.firstBusinessHint')}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Stack>
                    <Typography variant="body1">{t('customers.fields.createFirstBusiness')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.fields.createFirstBusinessHelper')}
                    </Typography>
                  </Stack>
                  <Switch
                    checked={createFirstBusiness}
                    onChange={(event) => setCreateFirstBusiness(event.target.checked)}
                  />
                </Stack>
                {errorAlert}
              </Stack>
            </CardContent>
            {actions}
          </Card>
        )}
      </Stack>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
